import debounce from "lodash/debounce";
import React from "react";
import {
    VisuallyHidden,
    useFocusVisible,
    useFocusWithin,
    useHover,
    useObjectRef,
} from "react-aria";
import { ButtonContext } from "react-aria-components";
import { t } from "ttag";

import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { Clickable } from "../Clickables";
import { Svg } from "../Svg";
import { useAutoPlay } from "./hooks/useAutoPlay";
import { useMatchMedia } from "./hooks/useMatchMedia";
import { usePageHidden } from "./hooks/usePageHidden";

import styles from "./Carousel.module.scss";
import dotStyles from "./Dots.module.scss";

/**
 * A carousel must always have a label. Either pass in a `label` which will be
 * inserted as visually hidden text, or create an element outside of the carousel
 * that describes the carousel and pass its ID with `labelledBy`.
 */
type CarouselLabel =
    | { label: string; labelledBy?: never }
    | { label?: never; labelledBy: string };

/**
 * Methods a consumer can call on the carousel directly, obtained by passing
 * `apiRef`. This suits one-shot calls from event handlers, like advancing
 * when a video ends: a prop can't express "do this once"; it would have to
 * be flipped on and then flipped back off.
 */
export interface CarouselApi {
    /**
     * Advance to the next slide if autoplay is currently running.
     * `onAutoPlayRunningChange` reports when autoplay resumes and the call
     * can be made again.
     */
    autoAdvance: () => void;
}

export type CarouselProps = React.PropsWithChildren<
    CarouselLabel & {
        loop?: boolean;
        advanceBy?: "page" | "item";
        /** Enable autoplay. `true` uses the default 5 000 ms interval; a number sets a custom interval in ms. */
        autoPlay?: boolean | number;
        /**
         * Autoplay continues, but the interval timer stands down and the
         * consumer paces the advancing instead, via `autoAdvance()`.
         */
        autoPlayExternallyPaced?: boolean;
        apiRef?: React.Ref<CarouselApi>;
        /** Pause autoplay when hovering over slides. Default: true. */
        pauseAutoPlayOnHover?: boolean;
        /** Called when the user explicitly toggles the autoplay pause button. */
        onAutoPlayPausedChange?: (isPaused: boolean) => void;
        /**
         * Used to pause slide media in lockstep with rotation.
         * (Externally paced autoplay still counts as running.)
         */
        onAutoPlayRunningChange?: (isRunning: boolean) => void;
        controls?:
            | null
            | React.ReactNode
            | ((props: { allVisible: boolean }) => React.ReactNode);
        dots?:
            | null
            | React.ReactNode
            | ((props: {
                  allVisible: boolean;
                  slideCount: number;
                  pageCount: number | undefined;
              }) => React.ReactNode);
        onScroll?: (e: React.UIEvent<HTMLDivElement>) => void;
        containerProps?: React.ComponentPropsWithRef<"div">;
        scrollContainerProps?: React.ComponentPropsWithRef<"div">;
        slideWrapperProps?: React.ComponentPropsWithRef<"div">;
        slideContainerProps?: React.ComponentPropsWithRef<"div">;
        onActiveChange?: (activeIndexes: Set<number>) => void;
    }
>;

function useDebouncedValue<T>(value: T, delay: number) {
    const [debouncedValue, setDebouncedValue] = React.useState(value);
    const debouncedSetValue = React.useMemo(
        () => debounce(setDebouncedValue, delay),
        [delay],
    );
    React.useEffect(() => {
        debouncedSetValue(value);
    }, [value, debouncedSetValue]);
    return debouncedValue;
}

function LiveRegion({ contents }: { contents: string }) {
    const debouncedContents = useDebouncedValue(contents, 100);
    return (
        <VisuallyHidden aria-live="polite" aria-atomic="true">
            {debouncedContents}
        </VisuallyHidden>
    );
}

/* eslint-disable */
function formatIndexes(
    indexes: Set<number>,
    total: number,
    label?: string,
): string {
    const listFormat =
        // @ts-expect-error: Not aware of `ListFormat`
        typeof Intl.ListFormat !== "undefined"
            ? // @ts-expect-error: 🙄
              new Intl.ListFormat("en")
            : null;
    const items = listFormat
        ? listFormat.format(Array.from(indexes).map((i) => (i + 1).toString()))
        : Array.from(indexes)
              .map((i) => (i + 1).toString())
              .join(", ");
    const noun = indexes.size === 1 ? t`slide` : t`slides`;
    return label
        ? t`Showing ${noun} ${items} of ${total} in carousel ‘${label}’`
        : t`Showing ${noun} ${items} of ${total}`;
}
/* eslint-enable */

/**
 * Builds ButtonContext slots for page-based dot navigation.
 *
 * When advanceBy is "page", the Carousel groups slides into "pages", and dots
 * represent pages rather than individual slides. An external Dots component can
 * render `page-{i}` slot buttons when it receives a pageCount via render prop.
 *
 * Page count comes from useScrollMetrics rather than from the number of visible
 * slides, because visible slide count can fluctuate mid-scroll as slides cross
 * the IntersectionObserver threshold.
 *
 * It updates when IntersectionObserver fires, not continuously during scroll.
 */
function buildPageSlots({
    scrollContainer,
    pageCount,
    activeIndexes,
    slideCount,
}: {
    scrollContainer: React.RefObject<HTMLElement | null>;
    pageCount: number | undefined;
    activeIndexes: Set<number>;
    slideCount: number;
}) {
    if (pageCount === undefined) return [];
    const scroller = scrollContainer.current;

    // scrollLeft math can't reach the last page because max scroll doesn't
    // fill a full page width, so check if the last slide is visible instead.
    const isLastPage = activeIndexes.has(slideCount - 1);
    const pageIndex =
        scroller && scroller.clientWidth > 0
            ? Math.round(scroller.scrollLeft / scroller.clientWidth)
            : 0;
    const activePage = isLastPage ? pageCount - 1 : pageIndex;

    const makeSlot = (index: number) => ({
        ["aria-disabled"]: index === activePage,
        onPress: () => {
            if (!scroller || index === activePage) return;
            scroller.scrollTo({
                left: index * scroller.clientWidth,
                behavior: "smooth",
            });
        },
        children: (
            <VisuallyHidden>{`${index + 1} of ${pageCount}`}</VisuallyHidden>
        ),
        ["data-active"]: index === activePage ? true : undefined,
    });

    return Array.from(
        { length: pageCount },
        (_, index) => [`page-${index}`, makeSlot(index)] as const,
    );
}

function eqSet(a: Set<unknown>, b: Set<unknown>) {
    if (a.size !== b.size) return false;
    for (const item of a) {
        if (!b.has(item)) return false;
    }
    return true;
}

/**
 * This is a hook that tracks the active slides in a carousel. It uses an
 * intersection observer to determine which slides are visible to the user.
 *
 * It expects the following attributes to be set on the slides:
 * - data-slide-index - The 0-indexed index of the slide
 * - aria-roledescription="slide"
 *
 * @param container - The container element that contains the slides.
 * @param threshold - The threshold for determining if a slide is visible.
 */
function useActiveSlides(
    container: React.RefObject<HTMLElement | null>,
    threshold: number,
) {
    const [state, setState] = React.useState(new Set<number>());

    React.useEffect(() => {
        if (!container.current) return;
        const elementsToWatch = container.current.querySelectorAll(
            "[aria-roledescription='slide']",
        );

        elementsToWatch.forEach((element) => {
            if (getComputedStyle(element).display === "contents") {
                console.error(
                    "useActiveSlides's IntersectionObserver doesn't see this element because `display: contents` is applied to it, and therefore renders it as `inert`.",
                    element,
                );
            }
        });

        const observer = new IntersectionObserver(
            (entries) => {
                setState((state) => {
                    const visibleEntryIds = new Set<number>(state);
                    for (const entry of entries) {
                        const key = Number(
                            (entry.target as HTMLElement).dataset.slideIndex,
                        );
                        if (entry.intersectionRatio > threshold) {
                            visibleEntryIds.add(key);
                        } else {
                            visibleEntryIds.delete(key);
                        }
                    }
                    // If the state has not changed return the original set so nothing is re-rendered
                    return eqSet(state, visibleEntryIds)
                        ? state
                        : visibleEntryIds;
                });
            },
            { threshold, root: container.current },
        );
        elementsToWatch.forEach((element) => observer.observe(element));

        return () => {
            observer.disconnect();
        };
    }, []);

    return state;
}

/**
 * Tracks whether all slides fit in the viewport and how many "pages" the scroll
 * content spans.
 *
 * Both are derived from scrollWidth / clientWidth via ResizeObserver on the
 * scroll container AND its slides wrapper. Observing the wrapper catches
 * scrollWidth changes from slide content loading or resizing, even when the
 * container itself doesn't resize.
 */
function useScrollMetrics(container: React.RefObject<HTMLElement | null>) {
    const [allVisible, setAllVisible] = React.useState(true);
    const [pageCount, setPageCount] = React.useState(1);
    React.useEffect(() => {
        const element = container.current;
        if (!element) return;
        function measure() {
            const { scrollWidth, clientWidth } = element!;
            setAllVisible(scrollWidth === clientWidth);
            setPageCount(
                clientWidth > 0 ? Math.ceil(scrollWidth / clientWidth) : 1,
            );
        }
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(element);
        // Also observe the slides wrapper so we catch scrollWidth changes
        // from slide content loading or resizing
        const slidesWrapper = element.firstElementChild;
        if (slidesWrapper) observer.observe(slidesWrapper);
        return () => observer.disconnect();
    }, []);
    return { allVisible, pageCount };
}

/**
 * Tracks conditions that should pause autoplay, per the W3C Carousel
 * Accessibility Practice Guide:
 * https://www.w3.org/WAI/ARIA/apg/patterns/carousel/
 *
 * - Hover is tracked via react-aria's `useHover` on the scroll container
 *   (slides only), so hovering over controls doesn't trigger a pause.
 *   Gated by the `pauseAutoPlayOnHover` prop.
 * - Focus is tracked via `useFocusWithin` + `useFocusVisible` so only
 *   keyboard-initiated focus pauses autoplay. Mouse clicks don't count —
 *   hover handles the mouse case.
 */
function useAutoPlayPauseConditions() {
    // Hover tracking — props are spread on the scroll container
    const { hoverProps, isHovered } = useHover({});

    // Focus tracking — only keyboard focus pauses autoplay.
    // useFocusWithin tracks any focus within the container;
    // useFocusVisible tells us if the focus came from the keyboard.
    const [isFocusWithin, setIsFocusWithin] = React.useState(false);
    const { focusWithinProps } = useFocusWithin({
        onFocusWithin: () => setIsFocusWithin(true),
        onBlurWithin: () => setIsFocusWithin(false),
    });
    const { isFocusVisible } = useFocusVisible();
    const isFocused = isFocusWithin && isFocusVisible;

    const isHidden = usePageHidden();
    const isReducedMotion = useMatchMedia("(prefers-reduced-motion: reduce)");

    return {
        isHovered,
        isFocused,
        isHidden,
        isReducedMotion,
        hoverProps,
        focusWithinProps,
    };
}

/**
 * A generic carousel component. Note: avoid using display:contents on slides!
 * It breaks IntersectionObserver, which this depends on.
 */
export function Carousel({
    children,
    label,
    labelledBy,
    loop = true,
    advanceBy = "page",
    autoPlay = false,
    autoPlayExternallyPaced = false,
    apiRef,
    pauseAutoPlayOnHover = true,
    onAutoPlayPausedChange,
    onAutoPlayRunningChange,
    controls,
    dots,
    onScroll,
    containerProps,
    scrollContainerProps,
    slideWrapperProps,
    slideContainerProps,
    onActiveChange,
}: CarouselProps) {
    const autoPlayEnabled = !!autoPlay;
    const autoPlayInterval = typeof autoPlay === "number" ? autoPlay : 5000;

    const container = useObjectRef(containerProps?.ref);
    const scrollContainer = useObjectRef(scrollContainerProps?.ref);
    const id = React.useId();
    const labelId = `${id}-label`;

    if (!labelledBy && (!label || label.includes("carousel")))
        console.warn(
            "The carousel label should not include the word 'carousel':\n\
                https://www.w3.org/WAI/ARIA/apg/patterns/carousel/#:~:text=Note%20that%20since%20the%20aria%2Droledescription%20is%20set%20to%20%22carousel%22%2C%20the%20label%20does%20not%20contain%20the%20word%20%22carousel%22.",
        );

    /**
     * The ratio of visibility that triggers the intersection observer.
     * This sets it in one spot and reads it in another, so this will keep
     * them linked.
     *
     * The value is somewhat arbitrary, and honestly it might need to be
     * configurable down the line. It was originally `1`, but that had problems
     * when the ratio was something like 0.9999956, and if the slide is a link,
     * a user might want to click that's 80% visible but would be unable to.
     */
    const activeIndexes = useActiveSlides(container, 0.6);
    React.useEffect(() => {
        onActiveChange?.(activeIndexes);
    }, [activeIndexes, onActiveChange]);

    const slideCount = React.Children.count(children);

    // If the first slide is active, we can't go back any further
    // so if we're looping, we can go to the last slide, otherwise we're disabled
    const prevScrollBehavior = activeIndexes.has(0)
        ? loop
            ? "last"
            : "disabled"
        : "adjacent";

    function goToPrevious() {
        const scroller = scrollContainer.current;
        if (!scroller) return;
        if (prevScrollBehavior === "last") {
            scroller.scrollTo({
                left: scroller.scrollWidth,
                behavior: "smooth",
            });
        } else {
            if (advanceBy === "page") {
                scroller.scrollBy({
                    left: -scroller.clientWidth,
                    behavior: "smooth",
                });
            } else {
                const slides = container.current?.querySelectorAll(
                    "[aria-roledescription='slide']",
                );
                if (!slides) return;
                // Find the slide immediately to the left of the first active slide
                for (const slide of slides) {
                    if (!(slide instanceof HTMLElement)) continue;

                    // Check if the slide is visible - since we're checking from left to right
                    // that would mean that the previous slide is the one to scroll to
                    if (activeIndexes.has(Number(slide.dataset.slideIndex))) {
                        const prev = slide.previousElementSibling;
                        if (!(prev instanceof HTMLElement)) continue;
                        scroller.scrollTo({
                            left: prev.offsetLeft,
                            behavior: "smooth",
                        });
                        break;
                    }
                }
            }
        }
    }

    // If the last slide is active, we can't go forward any further
    // so if we're looping, we can go to the first slide, otherwise we're disabled
    const nextScrollBehavior = activeIndexes.has(slideCount - 1)
        ? loop
            ? "first"
            : "disabled"
        : "adjacent";

    function goToNext() {
        if (nextScrollBehavior === "disabled") return;
        const scroller = scrollContainer.current;
        if (!scroller) return;
        if (nextScrollBehavior === "first") {
            scroller.scrollTo({ left: 0, behavior: "smooth" });
        } else {
            if (advanceBy === "page") {
                scroller.scrollBy({
                    left: scroller.clientWidth,
                    behavior: "smooth",
                });
            } else {
                const slides = container.current?.querySelectorAll(
                    "[aria-roledescription='slide']",
                );
                if (!slides) return;
                // From the left, find the first slide that is not active
                for (const slide of slides) {
                    const prev = slide.previousElementSibling;
                    if (
                        !(slide instanceof HTMLElement) ||
                        !(prev instanceof HTMLElement)
                    )
                        continue;

                    // Check if the previous slide is visible - since we're checking from left to right
                    // that would mean this slide is the one to scroll to
                    if (activeIndexes.has(Number(prev.dataset.slideIndex))) {
                        scroller.scrollTo({
                            left: slide.offsetLeft,
                            behavior: "smooth",
                        });
                        break;
                    }
                }
            }
        }
    }

    const { allVisible, pageCount: scrollPageCount } =
        useScrollMetrics(scrollContainer);
    const pageCount = advanceBy === "page" ? scrollPageCount : undefined;

    // Autoplay logic — Carousel owns all pause state internally
    const [isUserPaused, setIsUserPaused] = React.useState(false);
    const {
        isHovered,
        isFocused,
        isHidden,
        isReducedMotion,
        hoverProps,
        focusWithinProps,
    } = useAutoPlayPauseConditions();
    const effectivelyPaused =
        isUserPaused ||
        (pauseAutoPlayOnHover && isHovered) ||
        isFocused ||
        isHidden ||
        isReducedMotion;
    const autoPlayRunning =
        autoPlayEnabled && !effectivelyPaused && !allVisible && slideCount > 1;
    const shouldRunTimer = autoPlayRunning && !autoPlayExternallyPaced;

    const togglePause = React.useCallback(() => {
        const next = !isUserPaused;
        setIsUserPaused(next);
        onAutoPlayPausedChange?.(next);
    }, [isUserPaused, onAutoPlayPausedChange]);

    // Ref-stashed so the report effect fires only on actual state changes,
    // not on parents re-creating the callback each render.
    const reportAutoPlayRunning = React.useEffectEvent((isRunning: boolean) => {
        onAutoPlayRunningChange?.(isRunning);
    });
    React.useEffect(() => {
        reportAutoPlayRunning(autoPlayRunning);
    }, [autoPlayRunning]);

    React.useImperativeHandle(apiRef, () => ({
        autoAdvance: () => {
            if (!autoPlayRunning) return;
            goToNext();
        },
    }));

    const { resetTimer } = useAutoPlay({
        enabled: shouldRunTimer,
        interval: autoPlayInterval,
        onAdvance: goToNext,
        progressRef: autoPlayEnabled ? container : undefined,
    });

    // Reset the autoplay timer whenever the active slide changes.
    React.useEffect(() => {
        resetTimer();
    }, [activeIndexes]);

    const showDefaultAutoPlayControl =
        autoPlayEnabled && !allVisible && slideCount > 1;

    if (typeof controls === "function") {
        controls = controls({ allVisible });
    }

    if (typeof dots === "function") {
        // eslint-disable-next-line react-hooks/immutability -- reassigning the render-prop param to its resolved value; not a prop-object mutation
        dots = dots({ allVisible, slideCount, pageCount });
    }

    // eslint-disable-next-line react-hooks/refs -- buildPageSlots reads the scroll container's live geometry to compute pagination during render; intentional
    const pageSlots = buildPageSlots({
        scrollContainer,
        pageCount,
        activeIndexes,
        slideCount,
    });

    return (
        <div
            {...containerProps}
            {...focusWithinProps}
            role="group" // might need an option to make this a region
            aria-roledescription="carousel"
            aria-labelledby={labelledBy ?? labelId}
            data-carousel-all-visible={allVisible}
            ref={container}
        >
            {!labelledBy && (
                <VisuallyHidden id={labelId}>{label}</VisuallyHidden>
            )}
            <LiveRegion
                contents={formatIndexes(activeIndexes, slideCount, label)}
            />
            <ButtonContext
                value={{
                    slots: {
                        previous: {
                            onPress: goToPrevious,
                            ["aria-label"]:
                                prevScrollBehavior === "last"
                                    ? t`Go to last item`
                                    : t`Previous item`,
                            isDisabled: prevScrollBehavior === "disabled",
                        },
                        next: {
                            onPress: goToNext,
                            ["aria-label"]:
                                nextScrollBehavior === "first"
                                    ? t`Go to first item`
                                    : t`Next item`,
                            isDisabled: nextScrollBehavior === "disabled",
                        },
                        autoplay: {
                            onPress: togglePause,
                            ["aria-label"]: autoPlayRunning
                                ? t`Pause auto-rotation`
                                : t`Start auto-rotation`,
                        },
                    },
                }}
            >
                {controls === null || controls === false ? null : controls ===
                      true || typeof controls === "undefined" ? (
                    <div
                        className={styles.controls}
                        style={{ display: allVisible ? "none" : "block" }}
                    >
                        <Clickable
                            slot="previous"
                            className={concatClassNames([
                                styles.navArrow,
                                styles.previous,
                            ])}
                        >
                            <Svg name="arrow" />
                        </Clickable>
                        <Clickable slot="next" className={styles.navArrow}>
                            <Svg name="arrow" />
                        </Clickable>
                        {showDefaultAutoPlayControl && (
                            <Clickable
                                slot="autoplay"
                                className={styles.autoPlayControl}
                            >
                                <VisuallyHidden>
                                    {autoPlayRunning ? t`Pause` : t`Play`}
                                </VisuallyHidden>
                                <Svg
                                    name={autoPlayRunning ? "pause" : "play"}
                                    aria-hidden="true"
                                />
                            </Clickable>
                        )}
                    </div>
                ) : (
                    controls
                )}
            </ButtonContext>
            <div
                {...scrollContainerProps}
                {...hoverProps}
                className={concatClassNames([
                    styles.scrollContainer,
                    scrollContainerProps?.className,
                ])}
                ref={scrollContainer}
                onScroll={(e) => {
                    onScroll?.(e);
                    scrollContainerProps?.onScroll?.(e);
                }}
                tabIndex={allVisible ? undefined : 0}
            >
                <div
                    {...slideWrapperProps}
                    aria-live="off"
                    aria-atomic="false"
                    className={concatClassNames([
                        styles.slidesWrapper,
                        slideWrapperProps?.className,
                    ])}
                >
                    {React.Children.map(children, (child, index) => {
                        const slideId = `${id}-slide-${index}`;
                        const activeIdsSorted = Array.from(activeIndexes).sort(
                            (a, b) => a - b,
                        );
                        return (
                            <div
                                {...slideContainerProps}
                                role="group"
                                data-slide-index={index}
                                data-first-visible={
                                    index === activeIdsSorted[0]
                                }
                                data-last-visible={
                                    index ===
                                    activeIdsSorted[activeIdsSorted.length - 1]
                                }
                                inert={!activeIndexes.has(index)}
                                aria-roledescription="slide"
                                aria-label={
                                    dots === null || dots === false
                                        ? `${index + 1} of ${slideCount}`
                                        : undefined
                                }
                                aria-labelledby={
                                    dots === null || dots === false
                                        ? undefined
                                        : slideId
                                }
                                className={concatClassNames([
                                    styles.slide,
                                    slideContainerProps?.className,
                                ])}
                            >
                                {child}
                            </div>
                        );
                    })}
                </div>
            </div>

            <ButtonContext
                value={{
                    slots: Object.fromEntries([
                        ...React.Children.toArray(children).map((_, i) => {
                            const slideId = `${id}-slide-${i}`;
                            return [
                                `slide-${i}`,
                                {
                                    id: slideId,
                                    ["aria-disabled"]: activeIndexes.has(i),
                                    onPress: () => {
                                        const scroller =
                                            scrollContainer.current;
                                        if (activeIndexes.has(i) || !scroller)
                                            return;
                                        const slide = document.querySelector(
                                            `[aria-labelledby="${slideId}"]`,
                                        );
                                        if (!(slide instanceof HTMLElement))
                                            return;
                                        scroller.scrollTo({
                                            left: slide.offsetLeft,
                                            behavior: "smooth",
                                        });
                                    },
                                    children: (
                                        <VisuallyHidden>{`${i + 1} of ${slideCount}`}</VisuallyHidden>
                                    ),
                                    ["data-active"]: activeIndexes.has(i)
                                        ? true
                                        : undefined,
                                },
                            ] as const;
                        }),
                        ...pageSlots,
                    ]),
                }}
            >
                {dots === null || dots === false ? null : dots === true ||
                  typeof dots === "undefined" ? (
                    <div
                        className={dotStyles.root}
                        style={{ display: allVisible ? "none" : "block" }}
                        role="group"
                        aria-label={t`Choose slide to display`}
                    >
                        {React.Children.map(children, (_, i) => (
                            <Clickable
                                key={i}
                                slot={`slide-${i}`}
                                className={
                                    activeIndexes.has(i)
                                        ? dotStyles.dotInactive
                                        : dotStyles.dotActive
                                }
                            />
                        ))}
                    </div>
                ) : React.isValidElement(dots) ? (
                    React.cloneElement(dots, {
                        role: "group",
                        ["aria-label"]: t`Choose slide to display`,
                    } as React.HTMLProps<HTMLElement>)
                ) : (
                    <div role="group" aria-label={t`Choose slide to display`}>
                        {dots}
                    </div>
                )}
            </ButtonContext>
        </div>
    );
}
